import {
  TITLE_TXT,
  LABEL_TXT,
  FORM_MODAL_WIDTHS,
  GLOBAL_CONSTANTS,
  ERROR_MSGS,
  STATUS,
} from "../../../constants/constants";
import {
  emailRules,
  phoneNumberRules,
  capitalizeWords,
} from "../../../helpers/helpers";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useState } from "react";
import { Spin, Collapse, Image, Typography } from "antd";
import DetailSubHeader from "../../../components/Details/DetailSubHeader";
import Detail from "../../../components/Details/Detail";
import DetailHeaderWithEdit from "../../../components/Details/DetailHeaderWithEdit";
import ArrowDownIcon from "../../../assets/icons/arrow-down-icon.svg";
import CustomModal from "../../../components/CustomModal/CustomModal";
import ComAddressForm from "../../../components/CustomForms/ComAddressForm";
import NomineeForm from "../../../components/CustomForms/NomineeForm";
import dayjs from "dayjs";
import { PERM_CODE } from "../../../constants/PermissionCodes";

const { Title } = Typography;

const InvestorDetails = ({ invDetailedData }) => {
  const { orgId, status } = useParams();
  const { dropdownData } = useSelector((state) => state?.enqMngt);
  const { loading } = useSelector(
    (state) => state.investor,
  );
  const [activeKey, setActiveKey] = useState(["1"]);
  const [modal, setModal] = useState({
    type: null, // "editComAdd" | "editNominee"
    data: null, // hold address info
  });
  const { user } = useSelector((state) => state.auth);
  const userPrivileges = user?.privilege?.map((p) => p.privilegeCode) || [];

  const editAccess =
    (userPrivileges.includes(PERM_CODE.IAEDT) && status === STATUS.ACTIVE.ID) ||
    (userPrivileges.includes(PERM_CODE.IDEDT) &&
      status === STATUS.DEACTIVATED.ID) ||
    (userPrivileges.includes(PERM_CODE.IIEDT) && status === STATUS.INACTIVE.ID);

  const isIndividual =
    invDetailedData?.userDetails?.investorTypeId === "UTY1000001";
  const isCompany =
    invDetailedData?.userDetails?.investorTypeId === "UTY1000006";
  const isHUF = invDetailedData?.userDetails?.investorTypeId === "UTY1000002";
  const isNbfc = invDetailedData?.userDetails?.investorTypeId === "UTY1000008";

  const primaryBankDetails =
    invDetailedData?.bankDetails?.find(
      (detail) => !detail.isEscrow && detail.isPrimary,
    ) || {};
  const escrowBankDetails =
    invDetailedData?.bankDetails?.find((detail) => detail.isEscrow) || {};

  const panelStyle = {
    marginBottom: 24,
    borderRadius: 2,
    borderBottom: "none",
  };

  const primaryAddress = (
    <>
      <Detail
        label={LABEL_TXT.ADD1}
        value={invDetailedData?.primaryAdd?.address1}
        isVerified={invDetailedData?.kycDetails?.kycGstinValue?.isVerified}
      />
      <Detail
        label={LABEL_TXT.ADD2}
        value={invDetailedData?.primaryAdd?.address2}
      />
      <Detail
        label={LABEL_TXT.CITY}
        value={invDetailedData?.primaryAdd?.city}
      />
      <Detail
        label={LABEL_TXT.PIN}
        value={invDetailedData?.primaryAdd?.pinCode}
      />
      <Detail
        label={LABEL_TXT.STATE}
        value={invDetailedData?.primaryAdd?.state}
      />
    </>
  );

  const comAddress = (
    <>
      <Detail
        label={LABEL_TXT.ADD1}
        value={invDetailedData?.billingAdd?.address1}
      />
      <Detail
        label={LABEL_TXT.ADD2}
        value={invDetailedData?.billingAdd?.address2}
      />
      <Detail
        label={LABEL_TXT.CITY}
        value={invDetailedData?.billingAdd?.city}
      />
      <Detail
        label={LABEL_TXT.PIN}
        value={invDetailedData?.billingAdd?.pinCode}
      />
      <Detail
        label={LABEL_TXT.STATE}
        value={invDetailedData?.billingAdd?.state}
      />
    </>
  );

  const handleComAddEdit = () => {
    const data = {
      orgId,
      billingAddId: invDetailedData?.billingAdd?.addressId,
      billingAddLine1: invDetailedData?.billingAdd?.address1,
      billingAddLine2: invDetailedData?.billingAdd?.address2,
      billingCityName: invDetailedData?.billingAdd?.city,
      billingStateName: invDetailedData?.billingAdd?.state,
      billingZipCode: invDetailedData?.billingAdd?.pinCode,
    };
    setModal({ type: "editComAdd", data });
    setActiveKey((prev) => [...prev, "2"]);
  };

  const handleNomineeEdit = () => {
    const data = {
      orgId,
      name: invDetailedData?.nomineeDetails?.name || null,
      dob: invDetailedData?.nomineeDetails?.dob
        ? dayjs(invDetailedData?.nomineeDetails?.dob)
        : null,
      phoneNumber: invDetailedData?.nomineeDetails?.phoneNumber || null,
      relationId: invDetailedData?.nomineeDetails?.relationId || null,
    };
    setModal({ type: "editNominee", data });
  };

  const closeModal = () => setModal({ type: null, data: null });

  const collapseItems = [
    ...(isIndividual
      ? [
          {
            key: "1",
            label: TITLE_TXT.PRIM_ADD,
            children: primaryAddress,
            style: panelStyle,
          },
        ]
      : []),
    {
      key: "2",
      label: (
        <DetailHeaderWithEdit
          title={TITLE_TXT.COMM_ADD}
          handleEdit={handleComAddEdit}
        />
      ),
      children: comAddress,
      style: panelStyle,
    },
  ];

  return (
    <>
      <div className="details-card-container">
        <Spin spinning={loading}>
          <div className="details-card">
            <DetailSubHeader
              title={isHUF ? TITLE_TXT.KAR_DETS : TITLE_TXT.USER_DETS}
            />
            <div className="details-content">
              <Detail
                label={isHUF ? LABEL_TXT.KAR_NAME : LABEL_TXT.CONT_NAME}
                value={invDetailedData?.userDetails?.contactName}
              />

              <Detail
                label={isHUF ? LABEL_TXT.KAR_EMAIL : LABEL_TXT.CONT_EMAIL}
                value={invDetailedData?.userDetails?.email}
                name="email"
                placeholder={`Enter ${isHUF ? "Karta" : "Contact"} Email`}
                rules={emailRules(isHUF ? "Karta" : "Contact")}
                isVerified={invDetailedData?.kycDetails?.kycEmail?.isVerified}
                isInput={true}
                orgId={orgId}
                isInv={true}
              />

              <Detail
                label={isHUF ? LABEL_TXT.KAR_PH_NUM : LABEL_TXT.CONT_PHONE}
                value={invDetailedData?.userDetails?.phoneNumber}
                name="phoneNumber"
                placeholder={`Enter ${isHUF ? "Karta" : "Contact"} Phone Number`}
                rules={phoneNumberRules(isHUF ? "Karta" : "Contact")}
                isVerified={invDetailedData?.kycDetails?.kycMobile?.isVerified}
                isInput={true}
                orgId={orgId}
                isInv={true}
              />

              <Detail
                label={LABEL_TXT.DESIG}
                value={invDetailedData?.userDetails?.designation}
              />
              <Detail
                label={LABEL_TXT.INV_TYPE}
                value={invDetailedData?.userDetails?.investorType}
              />
            </div>

            <div className="detail-collapse-container">
              <Collapse
                bordered={false}
                activeKey={activeKey}
                onChange={(key) => setActiveKey(key)}
                expandIcon={({ isActive }) => (
                  <Image
                    src={ArrowDownIcon}
                    alt="Arrow"
                    preview={false}
                    style={{
                      transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.3s ease",
                    }}
                  />
                )}
                expandIconPosition="end"
                className="detail-collapse"
                items={collapseItems}
              />
            </div>
          </div>
        </Spin>
      </div>
      {/* Company details & Huf details */}
      {isCompany || isHUF || isNbfc ? (
        <div className="details-card-container">
          <Spin spinning={loading}>
            <div className="details-card">
              <DetailSubHeader
                title={
                  isHUF
                    ? TITLE_TXT.HUF_DETS
                    : isCompany
                      ? TITLE_TXT.COMP_DETS
                      : TITLE_TXT.NBFC_DETS
                }
              />
              <div className="details-content">
                <Detail
                  label={
                    isHUF
                      ? LABEL_TXT.HUF_NAME
                      : isCompany
                        ? LABEL_TXT.COMP_NAME
                        : LABEL_TXT.NBFC_NAME
                  }
                  value={invDetailedData?.invDetails?.busName}
                  isVerified={
                    invDetailedData?.kycDetails?.kycPanValue?.isVerified
                  }
                />
                <Detail
                  label={
                    isHUF
                      ? LABEL_TXT.HUF_EMAIL
                      : isCompany
                        ? LABEL_TXT.COMP_EMAIL
                        : LABEL_TXT.NBFC_EMAIL
                  }
                  value={invDetailedData?.invDetails?.email}
                  name="email"
                  placeholder={
                    isHUF
                      ? "HUF Email"
                      : isCompany
                        ? "Company Email"
                        : "NBFC Email"
                  }
                  rules={emailRules(isHUF ? "HUF" : "Company")}
                  isVerified={invDetailedData?.kycDetails?.kycEmail?.isVerified}
                  isInput={true}
                  orgId={orgId}
                  isInv={true}
                />

                <Detail
                  label={
                    isHUF
                      ? LABEL_TXT.HUF_PH_NUM
                      : isCompany
                        ? LABEL_TXT.COMP_PH_NUM
                        : LABEL_TXT.NBFC_PH_NUM
                  }
                  value={invDetailedData?.invDetails?.phoneNumber}
                  name="phoneNumber"
                  placeholder={
                    isHUF
                      ? "HUF Phone Number"
                      : isCompany
                        ? "Company Phone Number"
                        : "NBFC Phone Number"
                  }
                  rules={phoneNumberRules(isHUF ? "HUF" : "Company")}
                  isVerified={
                    invDetailedData?.kycDetails?.kycMobile?.isVerified
                  }
                  isInput={true}
                  orgId={orgId}
                  isInv={true}
                />

                <Detail
                  label={LABEL_TXT.IND_TYPE}
                  value={invDetailedData?.invDetails?.industryTypeId}
                  displayValue={invDetailedData?.invDetails?.industryTypeName}
                  name="industryTypeId"
                  placeholder="Select Industry Type"
                  options={dropdownData?.industryTypeList?.map((industry) => ({
                    value: industry.industryTypeId,
                    label: capitalizeWords(industry.industryTypeName),
                  }))}
                  rules={[
                    {
                      required: true,
                      message: ERROR_MSGS.SEL_IND_TYPE,
                    },
                  ]}
                  isEditable={editAccess}
                  orgId={orgId}
                  isInv={true}
                />
                {isCompany && (
                  <Detail
                    label={LABEL_TXT.COMP_TYPE}
                    value={invDetailedData?.invDetails?.businessTypeName}
                  />
                )}
                <Detail
                  label={LABEL_TXT.ANN_TURN}
                  value={invDetailedData?.invDetails?.annualTurnoverId}
                  displayValue={invDetailedData?.invDetails?.annualTurnoverName}
                  name="annualTurnoverId"
                  placeholder="Select Annual Turnover"
                  options={dropdownData?.annualTurnoverList?.map((item) => ({
                    value: item.annualTurnoverId,
                    label: capitalizeWords(item.annualTurnoverName),
                  }))}
                  rules={[
                    {
                      required: true,
                      message: ERROR_MSGS.SEL_ANN_TURN,
                    },
                  ]}
                  isEditable={editAccess}
                  orgId={orgId}
                  isInv={true}
                />
              </div>

              <div className="details-title">
                <DetailSubHeader title={TITLE_TXT.PRIM_ADD} />
              </div>

              <div className="details-content">
                <Detail
                  label={LABEL_TXT.ADD1}
                  value={invDetailedData?.primaryAdd?.address1}
                  isVerified={
                    invDetailedData?.kycDetails?.kycGstinValue?.isVerified
                  }
                />
                <Detail
                  label={LABEL_TXT.ADD2}
                  value={invDetailedData?.primaryAdd?.address2}
                />
                <Detail
                  label={LABEL_TXT.CITY}
                  value={invDetailedData?.primaryAdd?.city}
                />
                <Detail
                  label={LABEL_TXT.PIN}
                  value={invDetailedData?.primaryAdd?.pinCode}
                />
                <Detail
                  label={LABEL_TXT.STATE}
                  value={invDetailedData?.primaryAdd?.state}
                />
              </div>
            </div>
          </Spin>
        </div>
      ) : null}
      {/* Nominee details */}
      {isIndividual || isHUF ? (
        <div className="details-card-container">
          <Spin spinning={loading}>
            <div className="details-card">
              <DetailSubHeader
                title={TITLE_TXT.NOM_DETS}
                hasEdit={true}
                handleEdit={handleNomineeEdit}
              />
              <div className="details-content">
                <Detail
                  label={LABEL_TXT.NOM_NAME}
                  value={
                    invDetailedData?.nomineeDetails?.name || GLOBAL_CONSTANTS.NA
                  }
                />

                <Detail
                  label={LABEL_TXT.DOB}
                  value={
                    invDetailedData?.nomineeDetails?.dob || GLOBAL_CONSTANTS.NA
                  }
                />

                <Detail
                  label={LABEL_TXT.NOM_PH_NUM}
                  value={
                    invDetailedData?.nomineeDetails?.phoneNumber ||
                    GLOBAL_CONSTANTS.NA
                  }
                />
                <Detail
                  label={LABEL_TXT.RELATION}
                  value={
                    invDetailedData?.nomineeDetails?.relationName ||
                    GLOBAL_CONSTANTS.NA
                  }
                />
              </div>
            </div>
          </Spin>
        </div>
      ) : null}

      <div className="details-card-container">
        <div className="details-card">
          <DetailSubHeader title={TITLE_TXT.BANK_KYC_DETS} />
          <Spin spinning={loading}>
            <div className="details-content">
              <div className="sub-details-title">
                <Title level={5}>{TITLE_TXT.PRIM_BANK_DETS}</Title>
              </div>
              <Detail
                label={LABEL_TXT.ACC_HOL_NAME}
                value={primaryBankDetails?.accHolderName || GLOBAL_CONSTANTS.NA}
              />
              <Detail
                label={LABEL_TXT.ACC_NUM}
                value={primaryBankDetails?.accNum || GLOBAL_CONSTANTS.NA}
              />
              <Detail
                label={LABEL_TXT.ACC_IFSC}
                value={primaryBankDetails?.ifscCode || GLOBAL_CONSTANTS.NA}
              />
              <Detail
                label={LABEL_TXT.BANK_NAME}
                value={primaryBankDetails?.bankName || GLOBAL_CONSTANTS.NA}
              />
              <Detail
                label={LABEL_TXT.BRANCH_NAME}
                value={primaryBankDetails?.branchName || GLOBAL_CONSTANTS.NA}
              />
            </div>

            <div className="details-content">
              <div className="sub-details-title">
                <Title level={5}>{TITLE_TXT.ECW_BANK_DETS}</Title>
              </div>
              <Detail
                label={LABEL_TXT.ECW_BEN_ACC_NAME}
                value={escrowBankDetails?.accHolderName || GLOBAL_CONSTANTS.NA}
              />
              <Detail
                label={LABEL_TXT.ECW_BANK_ACC_NUM}
                value={escrowBankDetails?.accNum || GLOBAL_CONSTANTS.NA}
              />
              <Detail
                label={LABEL_TXT.ECW_IFSC_CODE}
                value={escrowBankDetails?.ifscCode || GLOBAL_CONSTANTS.NA}
              />
              <Detail
                label={LABEL_TXT.ECW_BANK_NAME}
                value={escrowBankDetails?.bankName || GLOBAL_CONSTANTS.NA}
              />
              <Detail
                label={LABEL_TXT.ECW_BRANCH_NAME}
                value={escrowBankDetails?.branchName || GLOBAL_CONSTANTS.NA}
              />
            </div>

            <div className="details-content">
              <div className="sub-details-title">
                <Title level={5}>{TITLE_TXT.KYC_DETS}</Title>
              </div>
              {(isCompany || isNbfc) && (
                <>
                  <Detail
                    label={isCompany ? LABEL_TXT.COMP_PAN : LABEL_TXT.NBFC_PAN}
                    value={invDetailedData?.kycDetails?.kycPanValue?.docValue}
                    isVerified={
                      invDetailedData?.kycDetails?.kycPanValue?.isVerified
                    }
                  />
                  <Detail
                    label={
                      isCompany ? LABEL_TXT.COMP_GSTIN : LABEL_TXT.NBFC_GSTIN
                    }
                    value={invDetailedData?.kycDetails?.kycGstinValue?.docValue}
                    isVerified={
                      invDetailedData?.kycDetails?.kycGstinValue?.isVerified
                    }
                  />
                  <Detail
                    label={
                      isCompany
                        ? LABEL_TXT.COMP_BANK_ACC
                        : LABEL_TXT.NBFC_BANK_ACC
                    }
                    value={invDetailedData?.kycDetails?.kycBank?.docValue}
                    isVerified={
                      invDetailedData?.kycDetails?.kycBank?.isVerified
                    }
                  />
                  <Detail
                    label={LABEL_TXT.DIR_PAN}
                    value={
                      invDetailedData?.kycDetails?.kycDirectorsPanValue
                        ?.docValue
                    }
                    isVerified={
                      invDetailedData?.kycDetails?.kycDirectorsPanValue
                        ?.isVerified
                    }
                  />
                  <Detail
                    label={LABEL_TXT.DIR_AADHAR}
                    value={
                      invDetailedData?.kycDetails?.kycDirectorsAadhaarValue
                        ?.docValue
                    }
                    isVerified={
                      invDetailedData?.kycDetails?.kycDirectorsAadhaarValue
                        ?.isVerified
                    }
                  />
                </>
              )}
              {isIndividual || isHUF ? (
                <>
                  {isHUF && (
                    <>
                      <Detail
                        label={LABEL_TXT.KAR_PAN}
                        value={
                          invDetailedData?.kycDetails?.kycDirectorsPanValue
                            ?.docValue
                        }
                        isVerified={
                          invDetailedData?.kycDetails?.kycDirectorsPanValue
                            ?.isVerified
                        }
                      />
                      <Detail
                        label={LABEL_TXT.HUF_PAN}
                        value={
                          invDetailedData?.kycDetails?.kycPanValue?.docValue
                        }
                        isVerified={
                          invDetailedData?.kycDetails?.kycPanValue?.isVerified
                        }
                      />
                      <Detail
                        label={LABEL_TXT.KAR_AADHAAR}
                        value={
                          invDetailedData?.kycDetails?.kycDirectorsAadhaarValue
                            ?.docValue
                        }
                        isVerified={
                          invDetailedData?.kycDetails?.kycDirectorsAadhaarValue
                            ?.isVerified
                        }
                      />
                    </>
                  )}
                  {isIndividual && (
                    <>
                      <Detail
                        label={LABEL_TXT.PAN}
                        value={
                          invDetailedData?.kycDetails?.kycPanValue?.docValue
                        }
                        isVerified={
                          invDetailedData?.kycDetails?.kycPanValue?.isVerified
                        }
                      />
                      <Detail
                        label={LABEL_TXT.AADHAR}
                        value={
                          invDetailedData?.kycDetails?.kycAadhaar?.docValue ||
                          GLOBAL_CONSTANTS.NA
                        }
                        isVerified={
                          invDetailedData?.kycDetails?.kycAadhaar?.isVerified
                        }
                      />
                    </>
                  )}

                  <Detail
                    label={LABEL_TXT.BANK}
                    value={
                      invDetailedData?.kycDetails?.kycBank?.docValue ||
                      GLOBAL_CONSTANTS.NA
                    }
                    isVerified={
                      invDetailedData?.kycDetails?.kycBank?.isVerified
                    }
                  />
                </>
              ) : null}

              {invDetailedData?.kycDetails?.platformSla?.docUrl && (
                <Detail
                  label={LABEL_TXT.SLA}
                  value={invDetailedData.kycDetails.platformSla.docUrl}
                  isVerified={
                    invDetailedData?.kycDetails?.platformSla?.isVerified
                  }
                  isDownloadBtn={true}
                />
              )}
            </div>
          </Spin>
        </div>
      </div>
      {modal.type && (
        <CustomModal
          title={
            modal.type === "editComAdd"
              ? TITLE_TXT.EDIT_COM_ADD
              : TITLE_TXT.EDIT_NOM_DETS
          }
          open
          centered
          width={FORM_MODAL_WIDTHS}
          content={
            modal.type === "editComAdd" ? (
              <ComAddressForm
                data={modal.data}
                onClose={closeModal}
                isInv={true}
              />
            ) : (
              <NomineeForm data={modal.data} onClose={closeModal} />
            )
          }
        />
      )}
    </>
  );
};

export default InvestorDetails;
